import { getRangeFromString, getEstimateReportData } from "../workbook";
import { getProcessedEstimate } from "../datasources/estimate";
import { NotFoundError } from "../../errors";
import {
  CHART_PREFIX,
  colorizeChart,
  getChartAndTable,
  getChartDimensions,
  getNextChartIndex,
  getTableTopLeftCellValue,
  readableSortFieldLabel,
  setChartTitleAndSubtitle,
  upsertChartTable,
} from "./helpers/common";
import {
  ChartOption,
  ChartOptionItem,
  ChartShape,
  ChartSize,
  EdiphiChartType,
  ChartIcon,
  ChartIconGetter,
} from "./api";

const COST_PER_SORT_CHART_TYPE = "costPerSort";
const costPerSortOptions: Record<string, ChartOption> = {
  sortField: {
    key: "sortField",
    label: "Sort By",
    type: "dropDown",
    selectedOptionItem: { key: "Bid Package", label: "Bid Package" },
    optionItems: {},
    getSelectedOptionItem: async function (_, chartName) {
      const optionItems = await getSortOptionItems();
      this.optionItems = optionItems;
      const sortFieldName = await getTableTopLeftCellValue(chartName);
      return (
        this.optionItems[sortFieldName] ||
        this.optionItems[this.selectedOptionItem.key] ||
        Object.values(this.optionItems)[0]
      );
    },
    onChange: async function (worksheetName, chartName, optionItem) {
      const currentOptions = await getOptions(worksheetName, chartName);
      const optionToUpdate = currentOptions[this.key];
      if (optionToUpdate) {
        optionToUpdate.selectedOptionItem = optionItem;
        await chartRefresher(worksheetName, chartName, currentOptions);
      }
    },
  },
  breakdown: {
    key: "breakdown",
    label: "Breakdown",
    type: "dropDown",
    selectedOptionItem: { key: "useGroup", suffix: "_$", label: "Use Group" },
    optionItems: {
      useGroup: { key: "useGroup", suffix: "_$", label: "Use Group" },
      unitCosts: { key: "unitCosts", suffix: "_uc", label: "Cost Type" },
    },
    getSelectedOptionItem: async function (worksheetName, chartName) {
      return Excel.run(async (context) => {
        const [_, table] = await getChartAndTable(context, worksheetName, chartName);
        const headerRange = table.getHeaderRowRange();
        headerRange.load("values");
        await context.sync();
        const headerValues = headerRange.values[0];
        const lastColumnHeaderName = headerValues[headerValues.length - 1];
        if (lastColumnHeaderName.endsWith("_$")) {
          return this.optionItems.useGroup;
        } else if (lastColumnHeaderName.endsWith("_uc")) {
          return this.optionItems.unitCosts;
        } else {
          return this.optionItems.useGroup;
        }
      });
    },
    onChange: async function (worksheetName: string, chartName: string, optionItem: ChartOptionItem) {
      const options = await getOptions(worksheetName, chartName);
      options.breakdown.selectedOptionItem = optionItem;
      await chartRefresher(worksheetName, chartName, options);
    },
  },
  includeBreakdowns: {
    key: "includeBreakdowns",
    label: "Include Breakdowns",
    type: "boolean",
    selectedOptionItem: { key: "true", label: "Yes" },
    optionItems: { true: { key: "true", label: "Yes" }, false: { key: "false", label: "No" } },
    getSelectedOptionItem: async function (worksheetName, chartName) {
      return Excel.run(async (context) => {
        const [chart] = await getChartAndTable(context, worksheetName, chartName);
        const firstSeries = chart.series.getItemAt(0);
        const dimStringResult = firstSeries.getDimensionDataSourceString("Values");
        await context.sync();

        const address = dimStringResult.value;
        const range = await getRangeFromString(context, address);
        if (!range) {
          return this.optionItems.true;
        }

        range.load("columnIndex");
        await context.sync();

        // If first series data is in second column (index 1), return false option
        // Otherwise return true option
        return this.optionItems[range.columnIndex === 1 ? "false" : "true"];
      });
    },
    onChange: async (worksheetName, chartName, optionItem) => {
      return Excel.run(async (context) => {
        const [chart, table] = await getChartAndTable(context, worksheetName, chartName);
        chart.load("title");
        await context.sync();
        const fullTitle = chart.title.text;
        const title = fullTitle.split("\n")[0];
        let subtitle = fullTitle.split("\n")[1];

        if (optionItem.key === "true") {
          chart.setData(table.getRange());
          await context.sync();
          const firstSeries = chart.series.getItemAt(0);
          firstSeries.delete();
          subtitle = "by use";
        } else {
          const range = table.getRange().getColumn(0).getResizedRange(0, 1);
          chart.setData(range);
          subtitle = "";
        }

        setChartTitleAndSubtitle(chart, title, subtitle);
        await context.sync();
        await colorizeChart(chart, context, false);
      });
    },
  },
  orientation: {
    key: "orientation",
    label: "Orientation",
    type: "toggle",
    selectedOptionItem: {
      key: "column",
      label: "",
      icon: { src: "assets/icons/FcBarChartStacked.svg", alt: "Bar Chart" },
    },
    optionItems: {
      column: {
        key: "column",
        label: "",
        icon: { src: "assets/icons/FcBarChartStacked.svg", alt: "Bar Chart" },
      },
      bar: {
        key: "bar",
        label: "",
        icon: { src: "assets/icons/FcBarChartStacked.svg", alt: "Bar Chart", rotated: true },
      },
    },
    getSelectedOptionItem: async function (worksheetName, chartName) {
      return Excel.run(async (context) => {
        const [chart] = await getChartAndTable(context, worksheetName, chartName);
        chart.load("chartType");
        await context.sync();

        if (chart.chartType === Excel.ChartType.columnStacked) {
          return this.optionItems["column"];
        } else if (chart.chartType === Excel.ChartType.barStacked) {
          return this.optionItems["bar"];
        }

        return this.optionItems["column"];
      });
    },
    onChange: async (worksheetName, chartName, optionItem) => {
      return Excel.run(async (context) => {
        const [chart] = await getChartAndTable(context, worksheetName, chartName);
        chart.chartType = optionItem.key === "column" ? Excel.ChartType.columnStacked : Excel.ChartType.barStacked;
        chart.axes.categoryAxis.textOrientation = optionItem.key === "column" ? 90 : 0;
        chart.axes.categoryAxis.reversePlotOrder = optionItem.key === "bar";
        chart.load("height,width");
        await context.sync();
        const height = chart.height;
        const width = chart.width;
        chart.height = width;
        chart.width = height;
        await context.sync();
      });
    },
  },
  showCodes: {
    key: "showCodes",
    label: "Labels",
    type: "toggle",
    selectedOptionItem: { key: "both", label: "both" },
    optionItems: {
      both: { key: "both", label: "both" },
      code: { key: "code", label: "code" },
      name: { key: "name", label: "name" },
    },
    getSelectedOptionItem: async function (_, chartName) {
      return Excel.run(async (context) => {
        const table = context.workbook.tables.getItem(chartName);
        const topLeftCell = table.getRange().getCell(0, 0);
        topLeftCell.load("values");
        await context.sync();
        const columnName = topLeftCell.values[0][0];

        if (columnName.endsWith("_code")) {
          return this.optionItems["code"];
        } else if (columnName.endsWith("_desc")) {
          return this.optionItems["name"];
        } else {
          return this.optionItems["both"];
        }
      });
    },
    onChange: async (worksheetName, chartName, optionItem) => {
      const currentOptions = await getOptions(worksheetName, chartName);
      const sortFieldOption = currentOptions["sortField"];
      const sortField = sortFieldOption?.selectedOptionItem.key;
      if (!sortFieldOption || !sortField) return;

      // For use_group, showCodes option doesn't apply - just refresh the chart
      if (sortField === "use_group") {
        await chartRefresher(worksheetName, chartName, currentOptions);
        return;
      }

      try {
        await Excel.run(async (context) => {
          const chartTable = context.workbook.tables.getItem(chartName);
          const reportData = await getEstimateReportData();
          const estimateId = reportData[0]?.estimate_id as string | undefined;
          if (!estimateId) return;
          const processed = await getProcessedEstimate(estimateId);
          const lineItems = processed?.baseBidItems ?? [];
          const filteredLineItems = lineItems.filter((item) => item.type === "line_item");

          const sortedLineItems = [...filteredLineItems].sort((a, b) => {
            const codeA = a[`${sortField}_code`] || "";
            const codeB = b[`${sortField}_code`] || "";
            return codeA.localeCompare(codeB);
          });

          const suffix = optionItem.key === "code" ? "_code" : optionItem.key === "name" ? "_desc" : "";
          const uniqueSortValues = Array.from(
            new Set(sortedLineItems.map((item) => item[`${sortField}${suffix}`]))
          ).filter(Boolean);

          // Update the first column of the chart table
          const firstCol = chartTable.columns.getItemAt(0);
          const dataRange = firstCol.getDataBodyRange();
          dataRange.values = uniqueSortValues.map((v) => [v]);
          firstCol.getHeaderRowRange().values = [[`${sortField}${suffix}`]];
          await context.sync();
        });
      } catch {
        // Row count mismatch can occur with label sort fields that share inherited codes — full rebuild
        await chartRefresher(worksheetName, chartName, currentOptions);
      }
    },
  },
};

const chartBuilder = async (options: Record<string, ChartOption>) => {
  const chartIndex = await getNextChartIndex();
  const chartName = `${CHART_PREFIX}${COST_PER_SORT_CHART_TYPE}_${chartIndex}`;
  const defaultOptions = costPerSortOptions;

  // Merge provided options with defaults
  const mergedOptions = Object.entries(defaultOptions).reduce(
    (acc, [key, defaultValue]) => {
      acc[key] = {
        ...costPerSortOptions[key],
        selectedOptionItem: options?.[key]?.selectedOptionItem || defaultValue.selectedOptionItem,
      };
      return acc;
    },
    {} as Record<string, ChartOption>
  );

  await upsertChartData(mergedOptions, chartName);
  const includeBreakdowns = mergedOptions.includeBreakdowns.selectedOptionItem.key === "true";
  const sortField = mergedOptions.sortField.selectedOptionItem.label;
  const breakdown = mergedOptions.breakdown.selectedOptionItem;
  const chartTitle = `Cost Per ${readableSortFieldLabel(sortField)}`;

  const chart = await addChart({
    chartType: Excel.ChartType.columnStacked,
    chartTitle,
    chartSubTitle: `by ${breakdown.label}`,
    name: chartName,
    shape: "landscape",
    size: "large",
    includeBreakdowns,
  });

  return chart;
};

const chartRefresher = async (worksheetName: string, chartName: string, options?: Record<string, ChartOption>) => {
  await upsertChartData(options || {}, chartName);
  await Excel.run(async (context) => {
    const [chart, table] = await getChartAndTable(context, worksheetName, chartName);

    const includeBreakdowns = options?.includeBreakdowns?.selectedOptionItem?.key === "true";
    const sortField = options?.sortField?.selectedOptionItem?.label || "Bid Package";
    const breakdown = options?.breakdown?.selectedOptionItem;
    const chartTitle = `Cost Per ${readableSortFieldLabel(sortField)}`;
    setChartTitleAndSubtitle(chart, chartTitle, `by ${breakdown?.label}`);

    if (includeBreakdowns) {
      chart.setData(table.getRange());
      await context.sync();
      const firstSeries = chart.series.getItemAt(0);
      firstSeries.delete();
    } else {
      const range = table.getRange().getColumn(0).getResizedRange(0, 1);
      chart.setData(range);
    }

    await context.sync();
    await colorizeChart(chart, context, false);
  });
};

const upsertChartData = async (options: Record<string, ChartOption>, chartName: string) => {
  const rawSortField = options?.sortField?.selectedOptionItem?.key || "Bid Package";
  const showCodes = options?.showCodes?.selectedOptionItem?.key || "both";
  const breakdown = options?.breakdown?.selectedOptionItem?.suffix || "_$";

  const createDataRows = async () => {
    const reportData = await getEstimateReportData();
    const estimateId = reportData[0]?.estimate_id as string | undefined;
    if (!estimateId) return [];
    const processed = await getProcessedEstimate(estimateId);
    const lineItems = processed?.baseBidItems ?? [];
    const filteredLineItems = lineItems.filter((item) => item.type === "line_item");

    // The default key "Bid Package" may not match the safe column name after
    // safeSortFieldColumnName transforms it (e.g. "sf_Bid Package"). Resolve to
    // the actual safe name, or fall back to the first available sort field.
    const safeColumnNames = [
      ...Object.values(processed?.sortFieldColumnNameMap ?? {}),
      ...Object.values(processed?.labelSortFieldColumnNameMap ?? {}),
    ];
    const sortField =
      rawSortField === "use_group" || safeColumnNames.includes(rawSortField)
        ? rawSortField
        : (safeColumnNames[0] ?? rawSortField);

    // Special handling for use_group sorting
    if (sortField === "use_group") {
      // Get all use group columns (ending in _$)
      const useGroupColumns = Object.keys(filteredLineItems[0] || {}).filter(
        (key) => key.endsWith("_$") && key !== "total_$"
      );

      const result: Record<string, string | number | null>[] = [];
      const useGroupTotals: Record<string, number> = {};

      // Calculate totals for each use group
      useGroupColumns.forEach((ugColumn) => {
        const ugName = ugColumn.replace("_$", "");
        useGroupTotals[ugName] = filteredLineItems.reduce((sum, item) => sum + (item[ugColumn] || 0), 0);
      });

      // Sort use groups by name
      const sortedUseGroups = Object.keys(useGroupTotals).sort((a, b) => a.localeCompare(b));

      const valueProperties = Object.keys(filteredLineItems[0] || {})
        .filter((key) => key.endsWith(breakdown) && key !== "total_uc")
        .map((key) => key.replace(breakdown, ""));

      for (const ugName of sortedUseGroups) {
        const row: Record<string, string | number | null> = {
          use_group: ugName,
          total: useGroupTotals[ugName],
        };

        for (const prop of valueProperties) {
          row[prop] = filteredLineItems.reduce((sum, item) => {
            if (breakdown === "_$") {
              return sum + (item[`${prop}${breakdown}`] || 0);
            } else {
              return sum + (item[`${prop}${breakdown}`] * item.quantity || 0);
            }
          }, 0);
        }

        result.push(row);
      }

      return result;
    }

    // Standard sorting for other fields
    const sortedLineItems = [...filteredLineItems].sort((a, b) => {
      const codeA = a[`${sortField}_code`] || "";
      const codeB = b[`${sortField}_code`] || "";
      return codeA.localeCompare(codeB);
    });

    const uniqueSortValues = Array.from(new Set(sortedLineItems.map((item) => item[sortField]))).filter(Boolean);

    const valueProperties = Object.keys(sortedLineItems[0] || {})
      .filter((key) => key.endsWith(breakdown) && key !== "total_uc")
      .map((key) => key.replace(breakdown, ""));
    const result: Record<string, string | number | null>[] = [];

    for (const sortValue of uniqueSortValues) {
      const firstMatch = sortedLineItems.find((item) => item[sortField] === sortValue);
      const code = firstMatch?.[`${sortField}_code`];
      const desc = firstMatch?.[`${sortField}_desc`];

      let displayValue = sortValue;
      let topLeftCellValue = sortField;
      if (showCodes === "code" && code) {
        displayValue = code;
        topLeftCellValue = `${sortField}_code`;
      } else if (showCodes === "name" && desc) {
        displayValue = desc;
        topLeftCellValue = `${sortField}_desc`;
      } else if (showCodes === "both" && code && desc) {
        displayValue = `${code} - ${desc}`;
      }

      const row: Record<string, string | number | null> = {
        [topLeftCellValue]: displayValue,
        total: sortedLineItems
          .filter((item) => item[sortField] === sortValue)
          .reduce((sum, item) => sum + (Number(item.total) || 0), 0),
      };

      for (const prop of valueProperties) {
        row[prop] = sortedLineItems
          .filter((item) => item[sortField] === sortValue)
          .reduce((sum, item) => {
            if (breakdown === "_$") {
              return sum + (item[`${prop}${breakdown}`] || 0);
            } else {
              return sum + (item[`${prop}${breakdown}`] * item.quantity || 0);
            }
          }, 0);
      }

      result.push(row);
    }

    return result;
  };

  const dataRows = await createDataRows();
  if (dataRows.length === 0) {
    console.warn("No data rows found for the chart.");
    return;
  }

  await upsertChartTable(dataRows, chartName, 1);
};

const addChart = async (params: {
  chartType: Excel.ChartType;
  chartTitle: string;
  chartSubTitle: string;
  name: string;
  shape: ChartShape;
  size: ChartSize;
  includeBreakdowns: boolean;
}) => {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const activeSheet = workbook.worksheets.getActiveWorksheet();
    const table = workbook.tables.getItemOrNullObject(params.name);
    await context.sync();
    if (table.isNullObject) {
      throw new NotFoundError("Table not found", { tableName: params.name });
    }

    let chart: Excel.Chart;

    if (params.includeBreakdowns) {
      chart = activeSheet.charts.add(params.chartType, table.getRange(), Excel.ChartSeriesBy.columns);
      const firstSeries = chart.series.getItemAt(0);
      firstSeries.delete();
    } else {
      const range = table.getRange().getColumn(0).getResizedRange(0, 1);
      await context.sync();
      chart = activeSheet.charts.add(params.chartType, range, Excel.ChartSeriesBy.columns);
    }

    chart.name = params.name;
    setChartTitleAndSubtitle(chart, params.chartTitle, params.chartSubTitle);

    const { width, height } = getChartDimensions(params.shape, params.size);
    chart.height = height;
    chart.width = width;
    styleChart(chart);
    await colorizeChart(chart, context, false);
    await context.sync();
    return chart;
  });
};

const styleChart = (chart: Excel.Chart) => {
  chart.axes.valueAxis.numberFormat = '[>999999]0.0,,"M";[>999]0,"k";0';
  chart.axes.categoryAxis.format.line.color = "black";
  chart.axes.categoryAxis.format.line.lineStyle = Excel.ChartLineStyle.continuous;
  chart.axes.categoryAxis.format.line.weight = 1;
  chart.axes.categoryAxis.textOrientation = 90;
  chart.axes.categoryAxis.format.font.size = 11;
  chart.axes.valueAxis.format.font.size = 10;
  chart.axes.valueAxis.format.font.bold = true;
  chart.legend.visible = true;
  chart.legend.position = Excel.ChartLegendPosition.bottom;
  chart.legend.format.font.size = 12;
  chart.legend.format.font.bold = true;
};

const getOptions = async (worksheetName: string, chartName: string): Promise<Record<string, ChartOption>> => {
  const options = { ...costPerSortOptions };

  for (const [key, option] of Object.entries(options)) {
    if (option.getSelectedOptionItem) {
      try {
        const currentValue = await option.getSelectedOptionItem(worksheetName, chartName);
        // Guarded because these option objects are shared: the spread above is shallow, so writing an
        // unresolved value here would replace the declared default until the module is re-imported.
        if (currentValue) options[key].selectedOptionItem = currentValue;
      } catch (error) {
        // ???: Change to logger.warn?
        console.warn(
          `Failed to get current value for option: '${key}' on chart '${worksheetName}: ${chartName}:'`,
          error
        );
      }
    }
  }

  return options;
};

const getSortOptionItems = async (): Promise<Record<string, ChartOptionItem>> => {
  const reportData = await getEstimateReportData();
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) return {};

  const processed = await getProcessedEstimate(estimateId);
  if (!processed) return {};

  const items: Record<string, ChartOptionItem> = {};
  Object.values(processed.sortFieldColumnNameMap).forEach((safeName) => {
    items[safeName] = { key: safeName, label: readableSortFieldLabel(safeName) };
  });
  Object.values(processed.labelSortFieldColumnNameMap).forEach((safeName) => {
    items[safeName] = { key: safeName, label: readableSortFieldLabel(safeName) };
  });

  // Mirror the _$ check in upsertChartData: items have _$ keys when use groups exist
  const hasUseGroupColumns =
    processed.baseBidItems.length > 0 && Object.keys(processed.baseBidItems[0]).some((k) => k.endsWith("_$"));
  if (hasUseGroupColumns) {
    items["use_group"] = { key: "use_group", label: "Use Group" };
  }

  return items;
};

const chartIconGetter: ChartIconGetter = (chartType?: Excel.ChartType): ChartIcon => {
  const chartTypeLower = chartType?.toString().toLowerCase() || "column";
  if (chartTypeLower.includes("column")) {
    return { src: "assets/icons/FcBarChartStacked.svg", alt: "Bar Chart" };
  } else {
    return { src: "assets/icons/FcBarChartStacked.svg", alt: "Bar Chart", rotated: true };
  }
};

export const costPerSortChartType: EdiphiChartType = {
  key: COST_PER_SORT_CHART_TYPE,
  title: "Cost Per Sort",
  chartBuilder,
  chartRefresher,
  chartIconGetter,
  getOptions,
};
